/**
 * Midoo 式引导课堂
 * ============================================================
 *
 * 与 ChatPage 的根本区别：
 *   ChatPage   —— 一个聊天窗口，学生自由说，AI 顺着聊。
 *   本页       —— **App 引导学生一步一步走**，每步一个明确任务，
 *                 说对了才进下一步。对话只是其中一步。
 *
 * 页面结构（对应真实 Midoo 的形态）：
 *   ┌─────────────────────────────┐
 *   │ ← 课程名            3/10    │  进度
 *   │ ▓▓▓▓▓░░░░░░░░░░            │
 *   ├─────────────────────────────┤
 *   │      [虚拟老师 · 会说话]      │  常驻顶部，说话时口型同步
 *   ├─────────────────────────────┤
 *   │  （当前步骤的内容区）         │  听 / 跟读 / 填空 / 换词 / 对话
 *   ├─────────────────────────────┤
 *   │      [ 主按钮 ]              │
 *   └─────────────────────────────┘
 *
 * 关于两种语言的声音：
 *   老师**普通话讲解**（开场）用 101020，**粤语示范**用 101019。
 *   TTS 由 /api/tts 按 language 参数选声，前端只管传对 language。
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import TeacherPortraitStage from '../components/TeacherPortraitStage';
import AbCompare from '../components/AbCompare';
import { splitByBlank, sentenceStateColor, SENTENCE_COLOR } from '../lib/sentenceStyle';
import { getTeacherAsset } from '../lib/teacherAssets';
import { loadTutorClips, resolveClipUrl, resolveTutorForDialect } from '../lib/tutorClip';
import { getSelectedDialectId } from '../lib/dialects';
import { useLipSync } from '../hooks/useLipSync';
import { useRecorder } from '../hooks/useRecorder';
import { fetchTutors, getSelectedTutorId, pickTutor } from '../lib/tutorUtils';
import { markLessonCompleted, getCompletedLessons, findNextLesson } from '../lib/courseProgress';
import { recognizeViaRelay } from '../lib/voiceRelay';

/** 需要学生开口的步骤类型 */
const SPOKEN_STEPS = ['repeat', 'fill', 'substitute', 'dialogue'];

/**
 * 逐字上色的颜色映射
 *   蓝 = 说对了   黄 = 过得去（同音异写/异体字）   红 = 没说对
 * 深浅两套是为了深色模式下的可读性。
 */
const COLOR_CLASS = {
  good: 'text-blue-600 dark:text-blue-400',
  ok: 'text-amber-500 dark:text-amber-400',
  bad: 'text-rose-600 dark:text-rose-400',
};

export default function LessonSessionPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 每一步的判定结果：{ [stepId]: { passed, matched, heardText, message } }
  const [results, setResults] = useState({});
  const [checking, setChecking] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [tutor, setTutor] = useState(null);
  // 老师立绘素材（路径来自 public/teachers/manifest.json，代码里不写死）
  const [teacherAsset, setTeacherAsset] = useState(null);
  const [tutorState, setTutorState] = useState('idle'); // idle | speaking | thinking
  const [speaking, setSpeaking] = useState(false);
  /*
   * 当前要播的口型片段。
   * 非空 → 老师舞台播该视频（自带我们的 TTS 音轨，音画天然同步）
   * 空值 → 走原来的 TTS + 立绘路径
   * 两条路互斥，见 speak() 里的分支说明。
   */
  const [tutorClipUrl, setTutorClipUrl] = useState(null);
  // 自动播放被浏览器拦截时记录待播内容，显示手动播放按钮
  const [autoplayBlocked, setAutoplayBlocked] = useState(null);

  const audioRef = useRef(null);
  /*
   * analysis: false —— **不做 Web Audio 分析**。
   *
   * 原因：createMediaElementSource() 会把 <audio> 输出路由进 Web Audio，
   * 之后必须靠 AudioContext 才能出声；AudioContext 在无用户手势时是
   * suspended 状态，结果是**完全静音**，而且 play() 仍会 resolve，
   * 前端根本察觉不到。曾因此出现"听不到老师声音"的问题。
   *
   * 口型功能已移除，不再需要音量数据，所以直接关掉这条路由，
   * 让音频走浏览器默认通路。立绘的"说话"指示改用 CSS 动画。
   */
  const { level, resume: resumeLipSync } = useLipSync(audioRef, {
    enabled: true,
    analysis: false,
  });

  /**
   * 录音 → 判定的时序问题（踩过一次坑，记录在这里）
   * ------------------------------------------------------------
   * useRecorder 的 getAudioFile 是 useCallback(..., [audioUrl])，
   * 也就是说**它只有在 audioUrl 这个 state 更新之后才拿得到音频**。
   * 而 audioUrl 是在 MediaRecorder 的 onstop 回调里才 set 的。
   *
   * 早期实现是「按下停止 → setTimeout(350ms) → 调 submitStep」，
   * 结果 submitStep 用的是**按下按钮那一刻捕获的旧闭包**，
   * 里面的 getAudioFile 还指向 audioUrl === null 的版本 → 永远返回 null，
   * 界面就报「没有录到声音，再试一次」。
   *
   * 正确做法（与 ChatPage 保持一致）：
   *   · 用 ref 跟踪最新的 audioUrl
   *   · 用 ref 跟踪最新的 submitStep（否则同样会拿到旧闭包）
   *   · 用 effect **等录音真正结束且音频就绪**再触发判定，而不是猜时间
   */
  const audioUrlRef = useRef(null);
  /*
   * 口型片段清单（同步查表用）。
   * 用 ref 而不是 state：speak 是 useCallback，查表发生在播放路径上，
   * 放进 state 会让 speak 的依赖数组频繁变化、连带重建所有下游回调。
   * 清单只在老师切换时变一次，ref 足够。
   */
  const clipsInfoRef = useRef(null);
  /*
   * 当前老师的 id。
   *
   * 为什么用 ref 而不是直接读 tutor 状态：speak() 是 useCallback，
   * 依赖数组里没有 tutor —— 直接读会拿到**挂载时的 null**，
   * 于是所有讲解都退回默认音色（女声）。这正是"男脸女声"的复现路径之一。
   * ref 每次渲染都是最新的，不受闭包过期影响。
   */
  const tutorRef = useRef(null);
  /*
   * 最近一次「本想播片段」的内容。
   *
   * 用途：片段可能因为浏览器自动播放策略而播不出来（带音轨的视频必须有
   * 用户手势）。这时要能**退回重试** —— 用户点一下就有了手势，视频才播得出来。
   * 没有它就只能静默回到立绘，用户看到的是「虚拟人闪一下就不动了」。
   */
  const pendingClipRef = useRef(null);
  /*
   * 片段清单是否已就绪。
   *
   * ⚠️ 这个 state 是为了修一个**时序竞态**：
   *   intro（开场白）是**进页面立即自动播放**的（0~1 秒内），
   *   而片段清单要先 fetchTutors 再读 manifest，是**嵌套的两跳异步**。
   *   开场白会抢在清单就绪之前查表 → 拿到空清单 → 回退实时 TTS。
   *   症状是「课程句子有口型、开场讲解永远没有」—— 而且没有任何报错，
   *   因为回退路径本身是正常工作的，只是白白丢掉了已经生成好的片段。
   *
   * 清单加载失败时也会置 true（下方 catch），否则会永远卡住不播。
   */
  const [clipsReady, setClipsReady] = useState(false);
  /*
   * 分段播放时的「等这一段播完」回调。
   *
   * speakSegments 需要**顺序**播放多段，所以要把 onClipEnded 包成 Promise 来 await。
   * 为 null 表示当前不是分段流程（单句 speak），此时 onClipEnded 走它自己的收尾逻辑。
   */
  const clipWaitRef = useRef(null);
  /*
   * 播放会话令牌 —— 谁最后认领，谁才有权说话。
   *
   * ⚠️ 这个令牌是为了修一个**串台 bug**（症状：切换步骤后虚拟人不动）：
   *
   *   speakSegments 是个 async 循环，它会 `await`「这一段片段播完」。
   *   如果学生在讲解播到一半时点了「开始上课」切到下一步，旧的循环**不会停** ——
   *   它仍然挂在那个 Promise 上。而 clipWaitRef 还指着它。
   *   于是下一步的片段播完时，handleClipEnded 看到 clipWaitRef 有值，
   *   就把结果交给了**上一步**的循环，同时把当前正在播的 video 卸掉
   *   （setTutorClipUrl(null)）—— 表现为「步骤切过去之后，老师就不动了」。
   *   90 秒超时后，那个旧循环还会继续跑，再清一次画面、把状态压成 idle。
   *
   * 现在：每次开始新的播放（speak / speakSegments / 进入新步骤）都先认领令牌，
   * 旧的循环在下一个检查点发现令牌不是自己的，就安静退出。
   */
  const speakTokenRef = useRef(0);
  const pendingCheckRef = useRef(false);
  const submitRef = useRef(null);

  const {
    isRecording,
    audioUrl,
    duration: recordingDuration,
    startRecording,
    stopRecording,
    resetRecording,
    getAudioFile,
  } = useRecorder();

  // 同步 audioUrl 到 ref
  //
  // ⚠️ 这个 effect 必须放在 useRecorder() 解构**之后**。
  //    依赖数组 [audioUrl] 是在渲染时同步求值的，如果 effect 写在解构之前，
  //    读取 audioUrl 会命中暂时性死区（TDZ）→ ReferenceError → 整个页面白屏。
  //    这个顺序曾经写反过一次，排查花了些时间，特此记录。
  useEffect(() => {
    audioUrlRef.current = audioUrl;
  }, [audioUrl]);

  /**
   * 打断老师语音。
   *
   * A/B 对比时必须先停老师再放自己的录音 —— 两段音重叠就完全失去对比意义，
   * 这恰恰是这个功能唯一的用途。老师的音频对象在页面内（audioRef），
   * 组件层拿不到，所以由页面暴露这个动作。
   */
  const stopSpeaking = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        /* 音频未就绪时设置 currentTime 会抛，忽略 */
      }
    }
    setTutorState('idle');
    setSpeaking(false);
  }, []);

  const step = session?.steps?.[stepIndex] || null;
  const totalSteps = session?.steps?.length || 0;
  const isSpoken = step && SPOKEN_STEPS.includes(step.type);

  // ---------- 加载课程步骤 ----------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // 已完成的课程用于开场白的「上节课我们学了…」
    // 用 courseProgress 的导出函数，而不是自己拼 localStorage 键名——
    // 键名（learn_cantonese_course_progress）是实现细节，写死容易改坏。
    let completed = [];
    try {
      completed = getCompletedLessons() || [];
    } catch {
      completed = [];
    }

    api
      .get(`/api/lesson-session/${lessonId}?completed=${encodeURIComponent(completed.join(','))}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (!data) throw new Error('课程不存在');
        setSession(data);
        setStepIndex(0);
        setResults({});
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message || '加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  // ---------- 加载虚拟老师 ----------
  useEffect(() => {
    let cancelled = false;
    fetchTutors()
      .then(async (list) => {
        if (cancelled) return;
        const dialectId = getSelectedDialectId();
        const persona = pickTutor(list, getSelectedTutorId());

        /*
         * 老师形象由**方言**决定，不是由用户选的老师决定。
         *
         * 原因：官方 TTS 音色有性别，而且部分方言**只有一种性别**——
         * 陕西(Marcus)/南京(Li)/闽南(Roy) 只有男声。用女立绘配男声，
         * 学生的第一反应是"配音配错了"，而不是"这是另一位老师"。
         * 所以形象跟着方言走，映射表在 manifest 的 dialectTutors 里。
         */
        const assetId = await resolveTutorForDialect(dialectId, persona?.id || 'aqing');
        if (cancelled) return;

        const asset = await getTeacherAsset(assetId).catch(() => null);
        if (cancelled) return;

        /*
         * 人设（怎么说话）仍用用户选的那位，但**形象与名字以素材为准** ——
         * 否则会出现「画面上是阿峰、名字写着阿清」。
         */
        const nextTutor = {
          ...(persona || {}),
          id: assetId,
          name: asset?.name || persona?.name || assetId,
          portrait: asset?.portrait || persona?.portrait || null,
        };
        tutorRef.current = nextTutor; // TTS 请求要用它选普通话音色
        setTutor(nextTutor);
        if (asset) setTeacherAsset(asset);

        // 口型片段清单：按**当前方言**取 —— 各方言的片段在不同子目录
        // （粤语在主目录，其余在 clips/<dialectId>/）。
        // 取不到就走 TTS + 立绘，同样不影响上课。
        loadTutorClips(assetId, dialectId)
          .then((info) => {
            if (cancelled) return;
            clipsInfoRef.current = info;
            setClipsReady(true); // 放行自动播放，理由见 clipsReady 的注释

            /*
             * 自检日志 —— 排查「虚拟人不动」时的第一手依据。
             *
             * 历史事故：开场白文本会随「已完成课程」变化（插入上节回顾），
             * 而片段是按文本哈希命名的 —— 文本一变就查不到，
             * 表现为「课程介绍阶段一帧不动」，而服务端一切正常。
             * 这类问题从界面上看和「功能没做」一模一样，只能靠这行日志区分。
             *
             * 如果条数明显偏少（粤语应为 156），先怀疑拿到了过期的清单 —— 硬刷新即可。
             */
            console.info(
              `[DialectGo] 口型片段已就绪：${assetId}｜${dialectId}｜${info.count} 条｜` +
                `${info.dir || '(该方言暂无片段)'}${info.optimistic ? '｜乐观模式' : ''}`
            );
          })
          .catch(() => {
            if (!cancelled) setClipsReady(true);
          });
      })
      .catch(() => {
        // 拿不到老师/素材也要放行自动播放，否则课永远不开始
        if (!cancelled) setClipsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- 播放老师的话（按 language 选声）----------
  /**
   * 认领一次新的播放，并释放上一次的等待。
   *
   * 每处「让老师说话」的入口都必须先调它 —— 这是消除串台的唯一开关。
   *
   * @returns {number} 本次的令牌；异步流程中途用它检查「我是否已被接管」
   */
  const claimPlayback = useCallback(() => {
    const finish = clipWaitRef.current;
    clipWaitRef.current = null;
    pendingClipRef.current = null;
    const token = speakTokenRef.current + 1;
    speakTokenRef.current = token;
    // 唤醒上一个还挂着的 await（它会发现令牌不符，随即安静退出）
    if (finish) finish({ ok: false, reason: 'superseded' });
    return token;
  }, []);

  const speak = useCallback(
    async (text, language = 'cantonese') => {
      if (!text) return;

      // 认领令牌：切步/连点都会让上一次的播放作废，避免两段声音或画面互相覆盖
      const token = claimPlayback();

      /*
       * ── 优先走预生成口型片段 ───────────────────────────────
       * 片段由 MuseTalk 按我们的 TTS 音频生成，收尾时把**同一条音频**
       * 封装进视频 —— 所以片段的音轨就是 TTS 本身。
       *
       * 因此这条路上**绝不能再播一遍 TTS**，否则两条音轨重叠，用户听到双重声音。
       * 这是整个方案唯一的硬约束。
       *
       * ── 片段优先，普通话也一样 ─────────────────────────────
       *
       * 早期这里写着「只对粤语尝试，普通话讲解没有对应片段」。
       * 这句注释后来过期了：现在每个方言都生成了 9 条普通话讲解/点评片段
       *（3 条课程介绍 + 6 条点评话术）。不放开的后果是讲解绕开片段、
       * 直接走实时 TTS —— 而实时 TTS 的音色曾经是全局女声，
       * 于是男老师的课一开头就是「男脸女声」。
       *
       * 命中就播片段（口型是照着这条音频生成的，最贴合）；
       * 没命中（例如点评句是「这次没听清「X」」拼出来的，文本不固定）
       * 就落到下面的 TTS 分支，那里会按老师性别选音色。
       */
      {
        const clipUrl = resolveClipUrl(text, clipsInfoRef.current);
        if (clipUrl) {
          // 掐掉可能还在播的上一句 TTS，否则它会和片段音轨叠在一起
          audioRef.current?.pause();
          // 记下来：万一自动播放被拦截，要能原样重试一次
          pendingClipRef.current = { text, language };
          setTutorClipUrl(clipUrl);
          setTutorState('speaking');
          setSpeaking(true);
          setAutoplayBlocked(null);
          return;
        }
      }

      // 走 TTS 路径前必须清掉片段，否则视频会停在最后一帧盖住立绘
      setTutorClipUrl(null);

      try {
        setTutorState('speaking');
        setSpeaking(true);
        setAutoplayBlocked(null);
        resumeLipSync?.();

        /*
         * 方言音频必须带上 dialect。
         *
         * 后端的 /api/tts 会按 dialect 查 config/dialects.js 里的
         * **方言专用音色**（四川=Sunny、陕西=Marcus、南京=Li、闽南=Roy、
         * 上海=Jada），并把 provider+voice 一起下发给 TTS。
         *
         * 不传 dialect 的话后端只能用默认普通话音色，方言文本就会被用
         * 普通话发音读出来 —— 等于教错发音。所以这不是可选项。
         *
         * ⚠️ 早期这里靠 instruction 传递方言，已被证伪（"张冠李戴"）：
         *    普通话音色 + instruction 只给普通话镀层薄口音，
         *    实测与普通话基线的声学距离只有 17.9，而专用音色是 22~32。
         *
         * 普通话讲解（language==='mandarin'）不带 dialect，走普通发音。
         * 但要带 **tutor** —— 普通话讲解的音色必须与老师性别一致，
         * 否则男老师的课上会出现「男脸女声」（用户反馈的原话）。
         * 后端据此读 manifest 的 tutor.mandarinVoice。
         */
        const dialect = language === 'mandarin' ? undefined : getSelectedDialectId();
        const resp = await api.post(
          '/api/tts',
          { text, language, dialect, tutor: tutorRef.current?.id },
          { responseType: 'blob' }
        );
        const url = URL.createObjectURL(resp.data);

        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.onended = () => {
            setTutorState('idle');
            setSpeaking(false);
            URL.revokeObjectURL(url);
          };
          audioRef.current.onerror = () => {
            setTutorState('idle');
            setSpeaking(false);
            URL.revokeObjectURL(url);
          };
          /*
           * 自动播放可能被浏览器拦截（首次交互前、或用户设置禁止自动播放）。
           * 原实现是 catch 后静默回到 idle —— 用户听不到声音却看不到任何提示，
           * 极难排查。这里改成**明确告知并提供手动播放**。
           */
          await audioRef.current.play().catch((err) => {
            setTutorState('idle');
            setSpeaking(false);
            setAutoplayBlocked({ text, language });
            console.warn('[课堂] 自动播放被拦截:', err?.name, err?.message);
          });
        }
      } catch (e) {
        console.warn('[课堂] 老师语音播放失败:', e.message);
        setTutorState('idle');
        setSpeaking(false);
      }
    },
    [resumeLipSync, claimPlayback]
  );

  /**
   * 播放一段片段并**等它播完**（分段播放用）
   *
   * 必须有超时兜底：万一 ended 事件没触发（解码异常、浏览器差异等），
   * 这个 Promise 会永远 pending，后面所有段都播不出来 ——
   * 表现为「老师说完第一段之后就再也不说话了」，而且完全没有报错。
   * 90 秒上限按最长讲解（约 20 秒）留足余量。
   */
  const playClipAndWait = useCallback(
    (clipUrl) =>
      new Promise((resolve) => {
        let settled = false;
        const finish = (info) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          clipWaitRef.current = null;
          resolve(info);
        };
        const timer = setTimeout(() => finish({ ok: false, reason: 'timeout' }), 90000);
        clipWaitRef.current = finish;
        setTutorClipUrl(clipUrl);
        setTutorState('speaking');
        setSpeaking(true);
      }),
    []
  );

  /**
   * 片段结束（播完 / 被拦截 / 加载失败）
   *
   * 用 useCallback 稳定引用：TeacherPortraitStage 把它放进 effect 依赖，
   * 若每次渲染都换新函数，effect 会反复触发、视频被反复 play()，
   * 表现为「刚播就被从头开始」的死循环。
   *
   * ⚠️ 关键：**播放被拦截时不能静默回到立绘**。
   * 带音轨的 <video> 在没有用户手势时会被浏览器策略拦下，而课程是自动推进的
   * （进入 demo 步骤就自动播），很容易撞上。原来的实现直接 setTutorClipUrl(null)
   * 收场，用户看到的就是「虚拟人闪一下、然后一直是静态图」—— 极像功能没做。
   * 现在改为复用既有的「点这里手动播放」兜底：用户点击产生手势后，
   * 同一段视频就能正常播放。
   */
  const handleClipEnded = useCallback((info) => {
    setTutorClipUrl(null);
    // 分段流程：把控制权交回 speakSegments，状态由它统一管理
    // （若在这里设 idle，段落之间会闪一下「待机」，观感很差）
    const finish = clipWaitRef.current;
    if (finish) {
      finish(info);
      return;
    }
    setTutorState('idle');
    setSpeaking(false);
    if (info && info.ok === false && pendingClipRef.current) {
      setAutoplayBlocked({ ...pendingClipRef.current, fromClip: true });
    }
  }, []);

  /**
   * 按「语言分段」依次播放老师的话
   *
   * 为什么需要分段：开场白是普通话讲解，但里面**嵌着粤语示范句**
   * （如「练的是「唔該，埋單。」」）。如果整段交给普通话音色，
   * 那个粤语句子会被用普通话音系读出来 —— 等于教了错的发音。
   * 后端把文本切成 [{text, language}] 分段，这里逐段用对应音色合成并顺序播放。
   *
   * @param {Array<{text:string, language:string}>} segments
   */
  const speakSegments = useCallback(
    async (segments, opts = {}) => {
      if (!Array.isArray(segments) || segments.length === 0) return;

      /*
       * 认领令牌 —— 本次播放从此独占画面与声音。
       * 上一个循环（可能还挂在 await 上）会在检查点发现令牌不符而退出，
       * 不会再来清掉本次的片段、也不会把状态压成 idle。
       */
      const token = claimPlayback();

      // 先清掉可能残留的片段，否则上一段的画面会停在最后一帧盖住立绘
      setTutorClipUrl(null);
      setTutorState('speaking');
      setSpeaking(true);
      setAutoplayBlocked(null);
      resumeLipSync?.();

      /*
       * ── 音频流水线：把"等下一段合成"藏进"正在播这一段"的时间里 ──────
       *
       * 原来的写法是严格串行：播完第 1 段 → 才去请求第 2 段的 TTS → 播。
       * API 在海外，单次合成 1~2 秒（命中服务端缓存也要 0.2 秒级），
       * 一段 4 句的讲解就要白等 4~8 秒，而这段时间画面是静止的 ——
       * 学生看到的就是「老师卡住了」。
       *
       * 现在：进入这一步就按顺序把需要 TTS 的段先取上（最多 3 条在途），
       * 轮到某段时它的音频通常已经到手。
       *
       * 为什么只开 3 条：TTS 有速率限制（40 次/窗口），
       * 长讲解全量并发会打满配额，反而让后面几段超时。
       */
      const ttsJobs = new Map(); // 段下标 → Promise<Blob>

      const startTtsJob = (i) => {
        if (ttsJobs.has(i)) return;
        const s = segments[i];
        if (!s?.text) return;
        const segLang = s.language || 'mandarin';
        const p = api
          .post(
            '/api/tts',
            {
              text: s.text,
              language: segLang,
              // 粤语示范段走当前方言；普通话讲解段不带（否则讲解也会被读成方言）
              dialect: segLang === 'mandarin' ? undefined : getSelectedDialectId(),
              // 普通话段要带 tutor：讲解音色必须与老师性别一致（后端读 mandarinVoice）
              tutor: tutorRef.current?.id,
            },
            { responseType: 'blob' }
          )
          .then((r) => r.data);
        // 失败也把 Promise 留在表里，消费处统一 catch，避免 unhandled rejection
        p.catch(() => {});
        ttsJobs.set(i, p);
      };

      /**
       * 这一段会不会走 TTS（有片段就不必合成）
       *
       * ⚠️ 清单没加载出来时**不预热**：那时 resolveClipUrl 一律返回空，
       *    会把"其实有片段"的段也送去合成 —— 白烧钱，而且结果根本用不上。
       */
      const willNeedTts = (s) => {
        if (!clipsInfoRef.current) return false;
        const info0 = opts.strict
          ? { ...clipsInfoRef.current, optimistic: false }
          : clipsInfoRef.current;
        return !resolveClipUrl(s?.text, info0);
      };

      let scanFrom = 0;
      const topUpPrefetch = () => {
        for (let i = scanFrom; i < segments.length; i += 1) {
          if (ttsJobs.has(i)) continue;
          if (willNeedTts(segments[i])) {
            startTtsJob(i);
            scanFrom = i + 1;
            return;
          }
        }
        scanFrom = segments.length;
      };

      for (let k = 0; k < 3; k += 1) topUpPrefetch();

      for (let segIdx = 0; segIdx < segments.length; segIdx += 1) {
        const seg = segments[segIdx];
        // 已被新的播放接管（切步 / 再点一次）→ 立刻退出，不要继续占着画面
        if (token !== speakTokenRef.current) return;
        if (!seg?.text) continue;

        /*
         * ── 片段优先 ──────────────────────────────────────────
         * 开场讲解现在也有片段：generate-clips.mjs 会把每课的 intro 文本
         * 一并生成（见 collectSentences 的第 2 条来源）。
         *
         * 这正是「课程介绍阶段虚拟人一动不动」的修复点 ——
         * intro 是每课里停留最久的一段，之前它只能走实时 TTS，
         * 于是虚拟人站着讲十几秒完全不张嘴。
         *
         * 有片段就播片段（自带音轨、音画天然同步），没有才回退 TTS。
         */
        /*
         * 查片段。
         *
         * ── strict 模式：只认清单里明确有的，不做乐观尝试 ──────────
         * 用于**动态文本**（LLM 现生成的点评、纠错反馈）。那类文本每次都不一样，
         * 永远不可能被预生成 —— 乐观尝试必然 404：白等一次网络往返，
         * 还会在控制台刷「加载失败」。用户看到满屏报错，会以为功能坏了。
         *
         * 但仍要查清单，不能直接跳过：点评里经常嵌粤语词（如「埋單」），
         * 那些词本来就有片段，用上它们画面才有口型。
         */
        const info =
          opts.strict && clipsInfoRef.current
            ? { ...clipsInfoRef.current, optimistic: false }
            : clipsInfoRef.current;

        const clipUrl = resolveClipUrl(seg.text, info);
        if (clipUrl) {
          const r = await playClipAndWait(clipUrl);

          // 等的过程中可能已经切了步骤：此时什么都不该做
          if (token !== speakTokenRef.current) return;

          if (r?.ok === false && r.reason === 'blocked') {
            /*
             * ⚠️ 分段路径**必须**自己处理拦截。
             *
             * 这里曾经漏掉了：handleClipEnded 在分段模式下直接把结果交给
             * speakSegments 就返回了，从不设置 autoplayBlocked ——
             * 于是开场讲解（进页面自动播、没有用户手势）被浏览器拦下后，
             * 既不出声也不出画，还**没有任何提示**。
             * 用户看到的就是「课程介绍阶段虚拟人完全没动作」，
             * 而点过「开始上课」（带手势）之后的粤语示范却正常 ——
             * 两者差别只在于有没有手势。
             *
             * 现在：中止整段并挂出可点入口。不继续后面几段是因为它们会以
             * 同样的原因失败，白等十几秒；交给统一兜底，用户点一下
             * 就有了手势，整段重播即可正常出声、出画面。
             */
            setTutorClipUrl(null);
            setTutorState('idle');
            setSpeaking(false);
            setAutoplayBlocked({ segments, fromClip: true });
            return;
          }

          // 正常播完 → 下一段；只有加载失败/超时才会落到下面的 TTS 回退
          if (r?.ok !== false) continue;
        }

        try {
          // 优先用在途的预取结果；没预取到（清单晚到 / 刚切步）就现取
          if (!ttsJobs.has(segIdx)) startTtsJob(segIdx);
          const blob = await ttsJobs.get(segIdx);
          // 合成期间可能已切步：丢弃这次结果，别把上一课的语音播出来
          if (token !== speakTokenRef.current) return;
          // 播之前就把下一条续上，保持流水线不断
          topUpPrefetch();
          const blobUrl = URL.createObjectURL(blob);
          // 逐段串行：必须等上一段播完再播下一段，否则会叠在一起
          await new Promise((resolve) => {
            const el = audioRef.current;
            if (!el) return resolve();
            el.src = blobUrl;
            el.onended = () => {
              URL.revokeObjectURL(blobUrl);
              resolve();
            };
            el.onerror = () => {
              URL.revokeObjectURL(blobUrl);
              resolve();
            };
            el.play().catch(() => resolve());
          });
        } catch (e) {
          console.warn('[课堂] 分段语音播放失败:', e.message);
        }
      }

      // 整段播完。若中途已被接管，就不要再动状态（那属于新的播放）
      if (token !== speakTokenRef.current) return;
      setTutorState('idle');
      setSpeaking(false);
    },
    [resumeLipSync, playClipAndWait, claimPlayback]
  );

  // ---------- 进入某一步时自动播放老师该说的话 ----------
  useEffect(() => {
    if (!step) return;
    /*
     * 等片段清单就绪再开始播。
     *
     * 开场白是**进页面就播**的，而清单是嵌套两跳的异步请求 ——
     * 不等的话，intro 必然抢在清单之前查表、永远走 TTS，
     * 刚生成好的讲解口型完全用不上。而且**没有任何报错**：
     * 回退路径本身工作正常，只是白丢了好东西，极难发现。
     * 清单加载通常几十毫秒；失败也会置 true，不会卡住。
     */
    if (!clipsReady) return;

    /*
     * 进入新步骤 → 作废上一步的播放。
     *
     * 不加这句的后果（真实发生过）：开场讲解播到一半点了「开始上课」，
     * 上一步的 async 循环不会被取消，仍挂在「等这段播完」上，
     * 稍后它继续执行，把这一步正在播的片段清掉、状态压回 idle ——
     * 表现为「切到下一步之后老师就不动了」。
     */
    claimPlayback();

    setFeedback(null);
    resetRecording();

    if (step.type === 'intro') {
      // 开场白必须按分段播：里面的粤语示范句要用粤语音色
      if (Array.isArray(step.speakSegments) && step.speakSegments.length) {
        speakSegments(step.speakSegments);
      } else {
        speak(step.speak?.text, 'mandarin'); // 兼容没有分段的旧数据
      }
    } else if (step.type === 'demo') {
      speak(step.speak?.text || step.phrase?.cantonese, 'cantonese');
    } else if (step.type === 'dialogue') {
      // 对话步骤：老师说它的第一句台词，学生回应
      const firstLine = session?.lesson?.phrases?.[0];
      const aiLine = firstLine ? firstLine.cantonese : '';
      if (aiLine) speak(aiLine, 'cantonese');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, session, clipsReady, claimPlayback]);

  // ---------- 提交这一步 ----------
  const submitStep = useCallback(
    async ({ textInput } = {}) => {
      if (!step) return;
      setChecking(true);
      setFeedback(null);
      try {
        const form = new FormData();
        form.append('lessonId', lessonId);
        form.append('stepId', step.id);

        if (textInput) {
          form.append('textInput', textInput);
        } else {
          const audioFile = await getAudioFile();
          if (!audioFile || audioFile.size === 0) {
            setFeedback({ type: 'warn', text: '没有录到声音，再试一次' });
            setChecking(false);
            return;
          }
          // 录音太短时给出更具体的提示，而不是笼统的"没录到声音"
          if (recordingDuration < 1) {
            setFeedback({ type: 'warn', text: '录得太短了，按住多说一会儿' });
            setChecking(false);
            return;
          }
          /*
           * ── 先在手机上把录音送去中国侧中继识别，只发**文字**给主服务 ──
           *
           * 判定必须在服务端做（步骤定义、关键词阈值都在服务端，不信前端），
           * 但"听写"这一步不需要绕美东：主服务在美东，音频送过去识别要
           * 多花 4~6 秒。中继在上海，实测 0.5~1.3 秒。
           *
           * 中继不可用（未开匿名登录/超时/失败）时自动回退为"传音频"，
           * 与改动前完全一致 —— 识别是学习链路的第一环，不能为了快承担不可用。
           */
          const relayed = await recognizeViaRelay(audioFile, getSelectedDialectId());
          if (relayed?.text) {
            form.append('textInput', relayed.text);
          } else {
            form.append('audio', audioFile);
          }
        }

        const res = await api.post('/api/lesson-step/check', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = res.data?.data;
        if (!data) throw new Error('判定失败');

        setResults((prev) => ({ ...prev, [step.id]: data }));
        setFeedback({
          type: data.passed ? 'ok' : 'warn',
          text: data.message,
          heard: data.heardText,
        });

        // 老师用**普通话**点评一句 —— Midoo 式课堂的关键交互。
        // 同样按分段播：点评里嵌的粤语词（如「埋單」）要用粤语音色读，
        // 否则粤语词会被读成普通话发音。
        //
        // strict：点评是 LLM 每次现生成的，不会有专属片段。
        // 不传这个的话，每次点评都会去请求一个不存在的文件（404），
        // 白等一次往返还在控制台刷错误 —— 见 speakSegments 里 strict 的说明。
        if (Array.isArray(data.teacherSegments) && data.teacherSegments.length) {
          speakSegments(data.teacherSegments, { strict: true });
        } else if (data.teacherComment) {
          speak(data.teacherComment, 'mandarin');
        }

        if (data.passed) setTutorState('idle');
        return data.passed;
      } catch (e) {
        console.error('[课堂] 步骤判定失败:', e.message);
        setFeedback({ type: 'warn', text: '判定失败，再试一次' });
        return false;
      } finally {
        setChecking(false);
      }
    },
    // 依赖必须包含 submitStep 内部真正用到的所有外部值：
    // speak（播放老师点评）、recordingDuration（录音过短判断），
    // 漏掉会让闭包取到旧值 —— 例如刚录完却用上一次的时长做判断。
    [step, lessonId, getAudioFile, speak, speakSegments, recordingDuration]
  );

  /** 录音按钮：点一下开始、再点一下停止（兼容移动端） */
  const handleRecord = useCallback(async () => {
    if (isRecording) {
      // 只负责停止；**判定交给下面的 effect**，在音频真正就绪后才发起
      pendingCheckRef.current = true;
      stopRecording();
    } else {
      setFeedback(null);
      pendingCheckRef.current = false;
      resetRecording();
      try {
        await startRecording();
      } catch (e) {
        console.error('[课堂] 无法开始录音:', e.message);
        setFeedback({ type: 'warn', text: '无法使用麦克风，请检查浏览器权限' });
      }
    }
  }, [isRecording, stopRecording, startRecording, resetRecording]);

  // 保持 submitRef 最新：必须声明在下面那个「录音结束触发判定」的 effect **之前**，
  // React 按声明顺序执行 effect，顺序反了同样会用到旧闭包。
  useEffect(() => {
    submitRef.current = submitStep;
  }, [submitStep]);

  // 录音结束 + 音频就绪 → 自动提交判定
  useEffect(() => {
    if (isRecording || !audioUrl || !pendingCheckRef.current) return;

    const timer = setTimeout(() => {
      if (pendingCheckRef.current && audioUrlRef.current && submitRef.current) {
        pendingCheckRef.current = false;
        submitRef.current({});
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isRecording, audioUrl, recordingDuration]);

  const goNext = useCallback(() => {
    setFeedback(null);
    resetRecording();
    // 切步时停掉老师的声音与片段，避免上一步的声音串到下一步
    audioRef.current?.pause();
    setTutorClipUrl(null);
    setTutorState('idle');
    setSpeaking(false);
    setStepIndex((i) => Math.min(i + 1, totalSteps - 1));
  }, [totalSteps, resetRecording]);

  const goPrev = useCallback(() => {
    setFeedback(null);
    resetRecording();
    audioRef.current?.pause();
    setTutorClipUrl(null);
    setTutorState('idle');
    setSpeaking(false);
    setStepIndex((i) => Math.max(i - 1, 0));
  }, [resetRecording]);

  // 到结算步骤时标记课程完成
  useEffect(() => {
    if (step?.type === 'score') {
      /*
       * 顺带上报这课的词句数：首页「最近 7 天」小结靠它算"学了几个词"。
       * 数字取自会话接口的 lesson.vocab / lesson.phrases（服务端权威数据），
       * 不在前端自己数步骤 —— 步骤里有讲解、判定等非词句项，数不准。
       */
      markLessonCompleted(lessonId, {
        vocab: session?.lesson?.vocab?.length || 0,
        phrases: session?.lesson?.phrases?.length || 0,
      });
    }
  }, [step, lessonId, session]);

  /*
   * 晋级信息：结算页的「下一课」入口。
   *
   * 为什么必须在**结算时**拉课程表，而不是进课就拉：
   *   「下一课」= 第一个未完成的课。当前这课要等 markLessonCompleted 写进
   *   localStorage 之后才算完成 —— 进课时算会把当前课当成"未完成"而返回它自己。
   *   上面的标记 effect 先声明，所以这里读到的已经是含本课的新进度。
   *
   * 为什么不用后端算：课程表本来就在前端（/api/curriculum），
   *   进度也只存在本地，多一个接口反而增加不一致的可能。
   */
  const [promotion, setPromotion] = useState(null);
  useEffect(() => {
    if (step?.type !== 'score') {
      setPromotion(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/curriculum', {
          params: { dialect: getSelectedDialectId() },
        });
        const data = res.data?.data || res.data;
        const groups = data?.groups || [];
        const done = new Set(getCompletedLessons());

        let total = 0;
        let completed = 0;
        let next = null;
        let levelInfo = null;

        for (const g of groups) {
          const lessons = g.lessons || [];
          for (const l of lessons) {
            total += 1;
            if (done.has(l.id)) completed += 1;
            if (!next && !done.has(l.id)) next = l;
          }
          // 当前课属于哪一单元（level），用于判断「本单元是否全部通关」
          if (levelInfo === null && lessons.some((l) => l.id === lessonId)) {
            levelInfo = {
              id: g.level?.id || '',
              name: g.level?.name || '',
              done: lessons.filter((l) => done.has(l.id)).length,
              total: lessons.length,
            };
          }
        }

        if (!cancelled) {
          setPromotion({
            next,
            // 全部学完时 next 为 null —— 结算页改推「去复习」
            allDone: !next && total > 0,
            total,
            completed,
            level: levelInfo,
          });
        }
      } catch {
        /* 拉不到课程表就退回旧的两个按钮，不影响结算 */
        if (!cancelled) setPromotion(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, lessonId]);

  const passedCount = useMemo(
    () => Object.values(results).filter((r) => r.passed).length,
    [results]
  );
  const spokenTotal = useMemo(
    () => (session?.steps || []).filter((s) => SPOKEN_STEPS.includes(s.type)).length,
    [session]
  );

  // ---------- 渲染 ----------
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#fdfaf1] dark:bg-zinc-900">
        <span className="material-icons !text-4xl animate-spin text-zinc-400">refresh</span>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-[#fdfaf1] px-8 dark:bg-zinc-900">
        <span className="material-icons !text-5xl text-zinc-300">error_outline</span>
        <p className="text-center text-sm text-zinc-500">{error || '课程不存在'}</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white active:opacity-80"
        >
          返回课程列表
        </button>
      </div>
    );
  }

  const progress = totalSteps > 1 ? (stepIndex / (totalSteps - 1)) * 100 : 0;

  return (
    <div className="flex h-screen flex-col bg-[#fdfaf1] dark:bg-zinc-900">
      {/* 隐藏的音频元素：老师的语音由它播放，useLipSync 从中读音量驱动口型 */}
      <audio ref={audioRef} className="hidden" />

      {/* 顶部：返回 + 进度 */}
      <header className="shrink-0 px-5 pt-5">
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center text-zinc-500 active:opacity-60"
            aria-label="返回"
          >
            <span className="material-icons">arrow_back</span>
          </button>
          <p className="flex-1 truncate px-2 text-center text-sm font-bold text-zinc-700 dark:text-zinc-200">
            {session.lesson.title}
          </p>
          <span className="w-6 text-right text-xs font-mono text-zinc-400">
            {stepIndex + 1}/{totalSteps}
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      {/*
        虚拟老师半身像：占据页面上半部分作为主视觉。

        与原来的圆形小头像（VirtualTutor）的分工：
          这里用大立绘做主视觉，VirtualTutor 保留给紧凑场景。
        状态与音量**复用** useLipSync 的 tutorState / level，没有另起一套。

        口型：**不做**。没有数字人后端，在静态照片上叠黑椭圆比不叠更糟。
        说话时用呼吸光环 + 音量波形表示"正在说话"，不假装嘴在动。
      */}
      <div className="shrink-0 px-5 pt-3">
        <TeacherPortraitStage
          tutor={tutor}
          asset={teacherAsset}
          state={tutorState}
          level={level}
          clipUrl={tutorClipUrl}
          onClipEnded={handleClipEnded}
        />

        {/*
          自动播放被拦截时的兜底。
          浏览器可能因"无用户手势"或用户设置拒绝自动播放。
          原实现 catch 后静默回到 idle —— 用户听不到声音却看不到任何提示，
          极难排查。这里显式告知并给出可点的播放按钮。
        */}
        {autoplayBlocked && (
          <button
            type="button"
            onClick={() => {
              const b = autoplayBlocked;
              setAutoplayBlocked(null);
              /*
               * 分段场景（开场讲解）要**整段重播**，不能只补一句 ——
               * 否则用户点了之后只听到第一句就又卡住。
               * 这一次点击会产生用户手势，后面的段都能正常自动播放。
               */
              if (Array.isArray(b.segments) && b.segments.length) speakSegments(b.segments);
              else speak(b.text, b.language);
            }}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-100 py-2.5 text-xs font-bold text-amber-800 active:scale-[0.99] dark:bg-amber-900/40 dark:text-amber-200"
          >
            <span className="material-icons !text-base">volume_up</span>
            {Array.isArray(autoplayBlocked.segments) && autoplayBlocked.segments.length
              ? '点这里听老师讲解'
              : autoplayBlocked.fromClip
                ? '浏览器拦截了自动播放 · 点这里看老师示范'
                : '浏览器拦截了自动播放 · 点这里听老师示范'}
          </button>
        )}
      </div>

      {/* 内容区 */}
      <main className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
        <StepContent
          step={step}
          session={session}
          feedback={feedback}
          result={results[step?.id]}
          results={results}
          comparison={results[step?.id]?.comparison}
          teacherComment={results[step?.id]?.teacherComment}
          isRecording={isRecording}
          recordingDuration={recordingDuration}
          checking={checking}
          speaking={speaking}
          onSpeak={speak}
          audioUrl={audioUrl}
          onStopTeacher={stopSpeaking}
          onSubmitText={(t) => submitStep({ textInput: t })}
        />
      </main>

      {/* 底部操作区 */}
      <footer className="shrink-0 px-5 pb-7 pt-2">
        <StepActions
          step={step}
          result={results[step?.id]}
          isRecording={isRecording}
          checking={checking}
          spokenTotal={spokenTotal}
          passedCount={passedCount}
          lessonId={lessonId}
          promotion={promotion}
          onRecord={handleRecord}
          onNext={goNext}
          onPrev={goPrev}
          onSpeak={speak}
          navigate={navigate}
        />
      </footer>
    </div>
  );
}

/* ============================================================
 * 内容区：按步骤类型渲染
 * ============================================================ */
export function StepContent({
  step,
  session,
  feedback,
  result,
  results,
  comparison,
  teacherComment,
  isRecording,
  recordingDuration,
  checking,
  speaking,
  onSpeak,
  audioUrl,
  onStopTeacher,
  onSubmitText,
}) {
  if (!step) return null;

  /** 本步是否已过关 —— 决定句子用绿色（完整/答对）还是中性色 */
  const passed = !!result?.passed;

  return (
    <div className="flex flex-1 flex-col">
      {/* 步骤标题 */}
      <p className="mb-3 text-center text-[11px] font-bold uppercase tracking-widest text-primary/70">
        {step.title}
      </p>

      {/*
        ── 判定难度提示 ──

        难度是会变的（先宽后紧），但学生看不出"为什么这节课变严了"时，
        会以为是 bug 或者自己变笨了。所以把档位和口径明说出来，
        只在**需要判定的步骤**上显示，避免整页都是标签。
      */}
      {step.strictness && Array.isArray(step.expect) && step.expect.length > 0 && (
        <p className="mb-3 flex items-center justify-center gap-1 text-[11px] text-zinc-400">
          <span className="material-icons !text-sm">tune</span>
          本步判定：{step.strictnessLabel || ''}
          {session?.progression?.strictnessTip ? ` · ${session.progression.strictnessTip}` : ''}
        </p>
      )}

      {/* ── 开场介绍 ── */}
      {step.type === 'intro' && (
        <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-zinc-800">
          <p className="text-[15px] leading-relaxed text-zinc-700 dark:text-zinc-200">
            {step.speak?.text}
          </p>
          {step.reviewedLesson && (
            <p className="mt-2 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <span className="material-icons !text-sm">history</span>
              已衔接上节课「{step.reviewedLesson.title}」
            </p>
          )}
        </div>
      )}

      {/* ── 老师示范 ── */}
      {step.type === 'demo' && step.phrase && (
        <div className="rounded-2xl bg-white p-5 text-center shadow-sm dark:bg-zinc-800">
          <p className="text-2xl font-bold leading-relaxed text-zinc-900 dark:text-zinc-50">
            {step.phrase.cantonese}
          </p>
          <p className="mt-2 font-mono text-sm text-primary">{step.phrase.jyutping}</p>
          <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">{step.phrase.mandarin}</p>
          <button
            type="button"
            onClick={() => onSpeak(step.phrase.cantonese, 'cantonese')}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-4 py-2 text-sm font-bold text-primary active:opacity-70"
          >
            <span className="material-icons !text-lg">volume_up</span>
            再听一次
          </button>
        </div>
      )}

      {/*
        ── 俚语 · 土话（熟练度够高才出现）──

        用独立卡片而不是复用示范句样式，因为两者的**要求不同**：
        示范句是要跟着读的，俚语只要求听懂。
        混在一起会让学生以为又得说一遍，反而加重负担。
      */}
      {step.type === 'demo' && step.slang && Array.isArray(step.items) && step.items.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5 shadow-sm dark:border-amber-900/40 dark:bg-amber-950/20">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-400">
            <span className="material-icons !text-base">auto_awesome</span>
            地道说法 · 听懂就行，不用马上会说
          </p>
          <div className="space-y-4">
            {step.items.map((it) => (
              <div key={it.term} className="rounded-xl bg-white/70 p-3 dark:bg-zinc-800/60">
                <div className="flex items-baseline gap-2">
                  <p className="text-xl font-bold text-zinc-900 dark:text-zinc-50">{it.term}</p>
                  <span className="text-xs text-amber-600 dark:text-amber-500">
                    {['', '常见', '地道', '本地人'][it.level] || ''}
                  </span>
                </div>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">{it.mandarin}</p>
                {it.usage && (
                  <p className="mt-1 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                    {it.usage}
                  </p>
                )}
                {it.example && (
                  <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-200">
                    {it.example}
                    {it.exampleMandarin && (
                      <span className="ml-2 text-xs text-zinc-400">（{it.exampleMandarin}）</span>
                    )}
                  </p>
                )}
                {it.caution && (
                  <p className="mt-1 text-xs text-rose-500 dark:text-rose-400">⚠️ {it.caution}</p>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              onSpeak(step.items.map((i) => i.example || i.term).join(' '), 'cantonese')
            }
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-4 py-2 text-sm font-bold text-amber-700 active:opacity-70 dark:text-amber-400"
          >
            <span className="material-icons !text-lg">volume_up</span>
            听一遍
          </button>
        </div>
      )}

      {/* ── 跟读 ── */}
      {step.type === 'repeat' && step.phrase && (
        <div className="rounded-2xl bg-white p-5 text-center shadow-sm dark:bg-zinc-800">
          <p className="mb-3 text-xs text-zinc-400">跟我读这一句</p>
          <p className={`text-2xl font-bold leading-relaxed ${sentenceStateColor(passed)}`}>
            {step.phrase.cantonese}
          </p>
          <p className="mt-2 font-mono text-sm text-primary">{step.phrase.jyutping}</p>

          {/*
            老师 / 我 对比试听。
            原来的「听示范」按钮被它取代 —— 老师那一侧就是同一个动作，
            留着会变成两个入口做同一件事。
          */}
          <AbCompare
            className="mt-4"
            teacherText={step.phrase.cantonese}
            audioUrl={audioUrl}
            onSpeak={onSpeak}
            speaking={speaking}
            onStopTeacher={onStopTeacher}
            disabled={checking}
          />
        </div>
      )}

      {/* ── 填空 ── */}
      {step.type === 'fill' && (
        <div className="rounded-2xl bg-white p-5 text-center shadow-sm dark:bg-zinc-800">
          <p className="mb-3 text-xs text-zinc-400">把缺掉的部分说出来</p>

          {/*
            挖空部分着蓝色（对齐 Midoo：蓝=待补全）。
            答对后换成完整句并整体转绿 —— 让"这句过了"一眼可见，
            而不是只靠底部按钮变绿来暗示。
          */}
          {passed && step.full ? (
            <p className={`text-2xl font-bold leading-relaxed ${SENTENCE_COLOR.good}`}>
              {step.full}
            </p>
          ) : (
            <p className={`text-2xl font-bold leading-relaxed ${SENTENCE_COLOR.pending}`}>
              {splitByBlank(step.prompt).map((seg, i) =>
                seg.type === 'blank' ? (
                  <span key={`b-${i}`} className={SENTENCE_COLOR.hint}>
                    {seg.value}
                  </span>
                ) : (
                  <span key={`t-${i}`}>{seg.value}</span>
                )
              )}
            </p>
          )}

          <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
            提示：{step.phrase?.mandarin}
          </p>

          <AbCompare
            className="mt-4"
            teacherText={step.phrase?.cantonese}
            audioUrl={audioUrl}
            onSpeak={onSpeak}
            speaking={speaking}
            onStopTeacher={onStopTeacher}
            disabled={checking}
          />
        </div>
      )}

      {/* ── 换词 ── */}
      {step.type === 'substitute' && (
        <div className="rounded-2xl bg-white p-5 text-center shadow-sm dark:bg-zinc-800">
          <p className="mb-3 text-xs text-zinc-400">{step.instruction}</p>

          {passed && step.full ? (
            <p className={`text-2xl font-bold leading-relaxed ${SENTENCE_COLOR.good}`}>
              {step.full}
            </p>
          ) : (
            <p className={`text-2xl font-bold leading-relaxed ${SENTENCE_COLOR.pending}`}>
              {splitByBlank(step.pattern).map((seg, i) =>
                seg.type === 'blank' ? (
                  <span key={`b-${i}`} className={SENTENCE_COLOR.hint}>
                    {seg.value}
                  </span>
                ) : (
                  <span key={`t-${i}`}>{seg.value}</span>
                )
              )}
            </p>
          )}

          <p className="mt-3 inline-block rounded-full bg-amber-50 px-3 py-1 text-sm font-bold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            换成「{step.target}」
          </p>
          {step.tip && <p className="mt-2 text-xs text-zinc-400">{step.tip}</p>}

          {/* 老师那侧放**换词后的完整句**，用户照着说才对得上 */}
          <AbCompare
            className="mt-4"
            teacherText={step.full || step.phrase?.cantonese}
            audioUrl={audioUrl}
            onSpeak={onSpeak}
            speaking={speaking}
            onStopTeacher={onStopTeacher}
            disabled={checking}
          />
        </div>
      )}

      {/* ── 对话 ── */}
      {step.type === 'dialogue' && (
        <div className="flex flex-1 flex-col justify-center gap-3">
          <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-zinc-800">
            <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-primary/70">
              {step.aiRole}
            </p>
            <p className="text-lg leading-relaxed text-zinc-800 dark:text-zinc-100">
              {session.lesson.phrases?.[0]?.cantonese || '……'}
            </p>
            <p className="mt-1 text-xs text-zinc-400">{session.lesson.phrases?.[0]?.mandarin}</p>
          </div>
          <p className="text-center text-xs text-zinc-400">
            现在轮到你了 —— 用刚学的句子回应他
          </p>
        </div>
      )}

      {/* ── 结算 ── */}
      {step.type === 'score' && (
        <ScoreCard session={session} results={results} />
      )}

      {/* 录音状态 */}
      {isRecording && (
        <div className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-red-50 py-3 dark:bg-red-900/25">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          <span className="font-mono text-sm text-red-600 dark:text-red-300">
            {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
          </span>
          <span className="text-xs text-red-500">正在录音…</span>
        </div>
      )}

      {checking && (
        <p className="mt-4 text-center text-xs text-zinc-400">正在判定…</p>
      )}

      {/*
        逐字上色：把学生说的字按准确度着色。
          蓝 = 说对了    黄 = 过得去（同音异写/异体字）    红 = 没说对
        颜色来自后端 compareToTarget()，依据是 ASR 识别出的字与目标字的对齐结果。
        之所以不用「发音分」上色：我们的声调分析准确率目前只有约 33%，
        用它上色会大面积误报，反而误导用户。
      */}
      {comparison?.chars?.length > 0 && (
        <div className="mt-4 rounded-2xl bg-white p-4 shadow-sm dark:bg-zinc-800">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-400">
              你说的
            </p>
            <span className="text-[11px] font-mono text-zinc-400">
              {comparison.summary.accuracy}%
            </span>
          </div>
          <p className="text-xl leading-relaxed tracking-wide">
            {comparison.chars.map((c, i) => (
              <span key={`${c.char}-${i}`} className={COLOR_CLASS[c.level] || COLOR_CLASS.bad}>
                {c.char}
              </span>
            ))}
          </p>
          <div className="mt-2.5 flex items-center gap-3 text-[11px] text-zinc-400">
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />准确
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />过得去
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-rose-500" />没说对
            </span>
          </div>
        </div>
      )}

      {/* 老师的普通话点评 */}
      {teacherComment && (
        <div className="mt-3 flex items-start gap-2 rounded-2xl bg-primary/5 px-4 py-3">
          <span className="material-icons !text-lg text-primary shrink-0">record_voice_over</span>
          <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-200">{teacherComment}</p>
        </div>
      )}

      {/* 判定反馈 */}
      {feedback && (
        <div
          className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
            feedback.type === 'ok'
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
          }`}
        >
          <p className="flex items-center gap-1.5 font-bold">
            <span className="material-icons !text-base">
              {feedback.type === 'ok' ? 'check_circle' : 'info'}
            </span>
            {feedback.text}
          </p>
          {feedback.heard && (
            <p className="mt-1 text-xs opacity-80">听到：{feedback.heard}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * 结算卡片
 * ============================================================ */
export function ScoreCard({ session, results = {} }) {
  // results 必须有默认值：它由外层 StepContent 透传，
  // 一旦哪次忘了传（这个 bug 真的发生过），结算页就会整页崩掉。
  // 默认空对象后，最坏情况只是显示 0 分，而不是白屏。
  const safeResults = results || {};
  const spokenSteps = session.steps.filter((s) => SPOKEN_STEPS.includes(s.type));
  const passed = spokenSteps.filter((s) => safeResults[s.id]?.passed).length;
  const rate = spokenSteps.length ? passed / spokenSteps.length : 1;
  const stars = rate >= 0.99 ? 3 : rate >= 0.6 ? 2 : 1;

  return (
    <div className="rounded-2xl bg-white p-5 text-center shadow-sm dark:bg-zinc-800">
      <p className="text-3xl">🎉</p>
      <p className="mt-1 text-lg font-bold text-zinc-800 dark:text-zinc-100">本课完成！</p>
      <p className="mt-2 text-2xl tracking-widest">
        {'⭐'.repeat(stars)}
        <span className="opacity-25">{'⭐'.repeat(3 - stars)}</span>
      </p>
      <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
        说对 {passed}/{spokenSteps.length} 个练习
      </p>

      {session.lesson.phrases?.length > 0 && (
        <div className="mt-4 rounded-xl bg-[#f8f6f6] p-3 text-left dark:bg-zinc-900/50">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
            本课要记住
          </p>
          {session.lesson.phrases.slice(0, 2).map((p) => (
            <p key={p.cantonese} className="text-sm text-zinc-700 dark:text-zinc-200">
              {p.cantonese}
              <span className="ml-1.5 font-mono text-xs text-primary">{p.jyutping}</span>
            </p>
          ))}
        </div>
      )}

      {session.lesson.cultureTip && (
        <p className="mt-3 text-left text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
          💡 {session.lesson.cultureTip}
        </p>
      )}
    </div>
  );
}

/* ============================================================
 * 底部按钮区
 * ============================================================ */
export function StepActions({
  step,
  result,
  isRecording,
  checking,
  spokenTotal,
  passedCount,
  lessonId,
  promotion,
  onRecord,
  onNext,
  onPrev,
  onSpeak,
  navigate,
}) {
  if (!step) return null;

  const bigBtn =
    'w-full rounded-2xl py-3.5 text-center text-base font-bold transition active:scale-[0.98] disabled:opacity-40';

  /*
   * 结算页：晋级到下一课。
   *
   * 对齐 Midoo 的闯关逻辑 —— 学完一关直接给「下一关」，不让用户
   * 回列表自己找。旧版只有「完成，返回到课程」且实际跳的是首页，
   * 学完一课之后没有任何"往前走"的指引，用户会以为到此为止了。
   *
   * 三种终局：
   *   1. 有下一课   → 主按钮「继续下一课」，带上课名
   *   2. 全部学完   → 主按钮改成「去复习巩固」
   *   3. 拉不到课程表（promotion 为空）→ 退回旧的"回课程列表"，不阻断
   */
  if (step.type === 'score') {
    const next = promotion?.next || null;
    const allDone = !!promotion?.allDone;
    const lv = promotion?.level;
    const levelCleared = lv && lv.total > 0 && lv.done >= lv.total;

    return (
      <div className="space-y-2">
        {/* 晋级信息：让用户看到自己走到哪了 */}
        {promotion && (
          <div className="rounded-xl bg-white/70 px-3 py-2 text-center text-[11px] text-zinc-500 dark:bg-zinc-800/60 dark:text-zinc-400">
            已通过 <b className="text-primary">{promotion.completed}</b> / {promotion.total} 课
            {lv ? `　·　${lv.name} ${lv.done}/${lv.total}` : ''}
          </div>
        )}

        {levelCleared && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-center text-xs font-bold text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            🏅 本单元已全部通关：{lv.name}
          </p>
        )}

        {next ? (
          <button
            type="button"
            onClick={() => {
              /*
               * 同一个路由组件换 :lessonId —— 页面不会卸载，
               * 但加载 effect 依赖 lessonId，会重新拉会话并把
               * stepIndex / results 归零（见上方加载逻辑）。
               * 这里再滚回顶部，避免停在上一课的结算位置。
               */
              navigate(`/session/${next.id}`);
              window.scrollTo({ top: 0 });
            }}
            className={`${bigBtn} bg-primary text-white shadow-md`}
          >
            继续下一课
            <span className="mt-0.5 block truncate text-xs font-normal opacity-90">
              {next.title}
            </span>
          </button>
        ) : allDone ? (
          <button
            type="button"
            onClick={() => navigate('/review')}
            className={`${bigBtn} bg-primary text-white shadow-md`}
          >
            🎉 全部学完 · 去复习巩固
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => navigate('/courses')}
          className={
            next || allDone
              ? 'w-full rounded-2xl py-2.5 text-sm font-bold text-zinc-500 active:opacity-60'
              : `${bigBtn} bg-primary text-white shadow-md`
          }
        >
          回到课程列表
        </button>

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="w-full rounded-2xl py-2.5 text-sm font-bold text-zinc-500 active:opacity-60"
        >
          再练一次
        </button>
      </div>
    );
  }

  // 讲解 / 示范：只需「继续」
  if (step.type === 'intro' || step.type === 'demo') {
    return (
      <button type="button" onClick={onNext} className={`${bigBtn} bg-primary text-white shadow-md`}>
        {step.type === 'intro' ? '开始上课' : '我听到了'}
      </button>
    );
  }

  // 需要开口的步骤：录音 + 判定
  if (SPOKEN_STEPS.includes(step.type)) {
    const passed = result?.passed;
    const tried = result != null; // 是否已经判过一次

    if (passed) {
      return (
        <button type="button" onClick={onNext} className={`${bigBtn} bg-emerald-500 text-white shadow-md`}>
          过了，继续 →
        </button>
      );
    }

    /*
     * 未过关时的按钮设计（依据用户反馈）：
     *   「如果不行就让学生重复一次……刚开始学生可能发音不准，
     *     也不要强制要求再来一次，直到非常准确为止，这样是不行的」
     *
     * 所以：**再试一次是主按钮，但"继续下一句"始终同等可见**，
     * 不做成隐蔽的小链接，也不设置必须通过才能前进的门槛。
     * 初学者本来就会不准，卡住只会让人不敢开口。
     */
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={onRecord}
          disabled={checking}
          className={`${bigBtn} ${
            isRecording ? 'bg-red-500 text-white' : 'bg-primary text-white shadow-md'
          }`}
        >
          {isRecording ? '■ 说完了' : checking ? '判定中…' : tried ? '🎤 再试一次' : '🎤 按住说'}
        </button>
        <button
          type="button"
          onClick={onNext}
          className="w-full rounded-2xl border border-zinc-200 bg-white py-3 text-sm font-bold text-zinc-600 transition active:scale-[0.98] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
        >
          继续下一句 →
        </button>
      </div>
    );
  }

  return null;
}
